import { gql } from 'graphql-request'
import { client } from './services/requests.service'

/**
 * Wait for service worker to be ready
 * @returns {Promise<boolean>} - Service worker ready status
 */
export const waitForServiceWorker = async () => {
  return new Promise((resolve) => {
    // Check if already ready
    if (window.serviceWorkerReady) {
      resolve(true)
      return
    }

    // Check if service worker is already controlling the page
    if (navigator.serviceWorker.controller) {
      window.serviceWorkerReady = true
      resolve(true)
      return
    }

    let attempts = 0
    const maxAttempts = 50 // 5 seconds max wait

    // Wait for service worker to be ready
    const checkReady = () => {
      attempts++

      if (window.serviceWorkerReady || navigator.serviceWorker.controller) {
        window.serviceWorkerReady = true
        resolve(true)
        return
      }

      if (attempts >= maxAttempts) {
        console.warn('🔔 FCM: Service worker wait timeout')
        resolve(false)
        return
      }

      // Check service worker registration status
      navigator.serviceWorker
        .getRegistration()
        .then((registration) => {
          if (registration) {
            if (registration.active) {
              window.serviceWorkerReady = true
              resolve(true)
              return
            }
          }
          setTimeout(checkReady, 100)
        })
        .catch((error) => {
          console.error('🔔 FCM: Error checking service worker registration:', error)
          setTimeout(checkReady, 100)
        })
    }

    // Start checking after a short delay
    setTimeout(checkReady, 100)
  })
}

/**
 * Request notification permission
 * @returns {Promise<boolean>} - Permission granted status
 */
export const requestNotificationPermission = async () => {
  try {
    if (!('Notification' in window)) {
      console.warn('⚠️ This browser does not support notifications')
      return false
    }

    if (Notification.permission === 'granted') {
      return true
    }

    if (Notification.permission === 'denied') {
      console.warn('⚠️ Notification permission denied')
      return false
    }

    const permission = await Notification.requestPermission()
    const granted = permission === 'granted'
    return granted
  } catch (error) {
    console.error('❌ Error requesting notification permission:', error)
    return false
  }
}

/**
 * Get FCM token from Firebase
 * @param {Object} app - Firebase app instance
 * @param {string} vapidKey - VAPID key
 * @param {Object} firebaseFunctions - Firebase functions (getMessaging, getToken)
 * @returns {Promise<string|null>} - FCM token or null
 */
export const getFCMToken = async (app, vapidKey, firebaseFunctions) => {
  try {
    if (!app || !vapidKey || !firebaseFunctions) {
      console.error('❌ Missing required parameters for FCM token')
      if (!vapidKey) {
        console.error('❌ VAPID key is missing. Please set VITE_FCM_VAPID_KEY environment variable')
        console.error(
          '❌ You can get the VAPID key from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates'
        )
      }
      return null
    }

    // The VAPID key should be a base64-encoded string. 80 characters is the minimum expected length for a valid base64-encoded VAPID public key as provided by Firebase.
    const VALID_VAPID_KEY_LENGTH = 80
    if (typeof vapidKey !== 'string' || vapidKey.length < VALID_VAPID_KEY_LENGTH) {
      console.error(
        '❌ Invalid VAPID key format. VAPID key should be a base64 string of at least 80 characters'
      )
      console.error('❌ Current VAPID key length:', vapidKey.length)
      return null
    }

    // Wait for service worker to be ready
    const swReady = await waitForServiceWorker()
    if (!swReady) {
      console.error('❌ Service worker not ready')
      return null
    }

    const { getMessaging, getToken } = firebaseFunctions

    const messaging = getMessaging(app)
    // Force serviceWorker registration to specific value, as the default is the name "firebase-messaging-sw.js" in root
    // But name/folder structure is not guaranteed after deployment build
    const serviceWorker = await navigator.serviceWorker.getRegistration()
    const token = await getToken(messaging, { vapidKey, serviceWorkerRegistration: serviceWorker })

    if (token) {
      return token
    } else {
      console.warn('⚠️ No FCM token available')
      return null
    }
  } catch (error) {
    console.error('❌ Error getting FCM token:', error)
    if (error.message && error.message.includes('applicationServerKey')) {
      console.error('❌ VAPID key validation failed. Please check your VITE_FCM_VAPID_KEY')
      console.error(
        '❌ VAPID key should be obtained from Firebase Console > Project Settings > Cloud Messaging'
      )
    }
    return null
  }
}

/**
 * Subscribe user to their personal FCM topic
 * @param {string} fcmToken - FCM token
 * @returns {Promise<boolean>} - Success status
 */
export const subscribeToUserTopic = async (fcmToken) => {
  try {
    if (!fcmToken) {
      console.error('❌ Missing fcmToken')
      return false
    }

    const mutation = gql`
      mutation SubscribeToUserTopic($fcmToken: String!) {
        subscribeToUserTopic(fcmToken: $fcmToken)
      }
    `

    const result = await client.request(mutation, {
      fcmToken,
    })

    return result.subscribeToUserTopic
  } catch (error) {
    console.error('❌ FCM: Error subscribing to topic:', error)
    return false
  }
}

/**
 * Subscribe staff user to site-specific check-in alerts topic
 * @param {string} fcmToken - FCM token
 * @param {string} siteId - Site ID
 * @returns {Promise<boolean>} - Success status
 */
export const subscribeToStaffCheckInAlerts = async (fcmToken, siteId) => {
  try {
    if (!fcmToken || !siteId) {
      return false
    }

    const mutation = gql`
      mutation SubscribeToStaffCheckInAlerts($fcmToken: String!, $siteId: ID!) {
        subscribeToStaffCheckInAlerts(fcmToken: $fcmToken, siteId: $siteId)
      }
    `

    const result = await client.request(mutation, { fcmToken, siteId })
    return result.subscribeToStaffCheckInAlerts
  } catch (_error) {
    return false
  }
}

/**
 * Unsubscribe staff user from site-specific check-in alerts topic
 * @param {string} fcmToken - FCM token
 * @param {string} siteId - Site ID
 * @returns {Promise<boolean>} - Success status
 */
export const unsubscribeFromStaffCheckInAlerts = async (fcmToken, siteId) => {
  try {
    if (!fcmToken || !siteId) {
      return false
    }

    const mutation = gql`
      mutation UnsubscribeFromStaffCheckInAlerts($fcmToken: String!, $siteId: ID!) {
        unsubscribeFromStaffCheckInAlerts(fcmToken: $fcmToken, siteId: $siteId)
      }
    `

    const result = await client.request(mutation, { fcmToken, siteId })
    return result.unsubscribeFromStaffCheckInAlerts
  } catch (_error) {
    return false
  }
}

/**
 * Subscribe staff user to site-specific terminal alerts topic
 * @param {string} fcmToken - FCM token
 * @param {string} siteId - Site ID
 * @returns {Promise<boolean>} - Success status
 */
export const subscribeToStaffTerminalAlerts = async (fcmToken, siteId) => {
  try {
    if (!fcmToken || !siteId) {
      return false
    }

    const mutation = gql`
      mutation SubscribeToStaffTerminalAlerts($fcmToken: String!, $siteId: ID!) {
        subscribeToStaffTerminalAlerts(fcmToken: $fcmToken, siteId: $siteId)
      }
    `

    const result = await client.request(mutation, { fcmToken, siteId })
    return result.subscribeToStaffTerminalAlerts
  } catch (_error) {
    return false
  }
}

/**
 * Unsubscribe staff user from site-specific terminal alerts topic
 * @param {string} fcmToken - FCM token
 * @param {string} siteId - Site ID
 * @returns {Promise<boolean>} - Success status
 */
export const unsubscribeFromStaffTerminalAlerts = async (fcmToken, siteId) => {
  try {
    if (!fcmToken || !siteId) {
      return false
    }

    const mutation = gql`
      mutation UnsubscribeFromStaffTerminalAlerts($fcmToken: String!, $siteId: ID!) {
        unsubscribeFromStaffTerminalAlerts(fcmToken: $fcmToken, siteId: $siteId)
      }
    `

    const result = await client.request(mutation, { fcmToken, siteId })
    return result.unsubscribeFromStaffTerminalAlerts
  } catch (_error) {
    return false
  }
}

/**
 * Subscribe staff user to site-specific equipment alerts topic
 * @param {string} fcmToken - FCM token
 * @param {string} siteId - Site ID
 * @returns {Promise<boolean>} - Success status
 */
export const subscribeToStaffEquipmentAlerts = async (fcmToken, siteId) => {
  try {
    if (!fcmToken || !siteId) {
      return false
    }

    const mutation = gql`
      mutation SubscribeToStaffEquipmentAlerts($fcmToken: String!, $siteId: ID!) {
        subscribeToStaffEquipmentAlerts(fcmToken: $fcmToken, siteId: $siteId)
      }
    `

    const result = await client.request(mutation, { fcmToken, siteId })
    return result.subscribeToStaffEquipmentAlerts
  } catch (_error) {
    return false
  }
}

/**
 * Unsubscribe staff user from site-specific equipment alerts topic
 * @param {string} fcmToken - FCM token
 * @param {string} siteId - Site ID
 * @returns {Promise<boolean>} - Success status
 */
export const unsubscribeFromStaffEquipmentAlerts = async (fcmToken, siteId) => {
  try {
    if (!fcmToken || !siteId) {
      return false
    }

    const mutation = gql`
      mutation UnsubscribeFromStaffEquipmentAlerts($fcmToken: String!, $siteId: ID!) {
        unsubscribeFromStaffEquipmentAlerts(fcmToken: $fcmToken, siteId: $siteId)
      }
    `

    const result = await client.request(mutation, { fcmToken, siteId })
    return result.unsubscribeFromStaffEquipmentAlerts
  } catch (_error) {
    return false
  }
}

/**
 * Unsubscribe user from their personal FCM topic
 * @param {string} fcmToken - FCM token
 * @returns {Promise<boolean>} - Success status
 */
export const unsubscribeFromUserTopic = async (fcmToken) => {
  try {
    if (!fcmToken) {
      console.error('❌ Missing fcmToken')
      return false
    }

    const mutation = gql`
      mutation UnsubscribeFromUserTopic($fcmToken: String!) {
        unsubscribeFromUserTopic(fcmToken: $fcmToken)
      }
    `

    const result = await client.request(mutation, {
      fcmToken,
    })

    return result.unsubscribeFromUserTopic
  } catch (error) {
    console.error('❌ FCM: Error unsubscribing from topic:', error)
    return false
  }
}
